import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const SlowModeSettings = () => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [interval, setInterval] = useState(2000); // Default 2 seconds
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Try to get settings from localStorage if available (as fallback)
        const savedSettings = localStorage.getItem('slow_mode_settings');
        if (savedSettings) {
          try {
            const parsed = JSON.parse(savedSettings);
            setIsEnabled(parsed.enabled);
            setInterval(parsed.interval);
          } catch (e) {
            console.warn('Failed to parse saved settings:', e);
          }
        }

        // Try to fetch from Supabase if the table exists
        const { data, error } = await supabase
          .from('application_settings')
          .select('value')
          .eq('key', 'slow_mode')
          .single();
          
        if (error) {
          // If error is not a "not found" error, then the table might not exist
          if (error.code === 'PGRST116') { // Not found error - key doesn't exist
            console.warn('Slow mode settings not found, using defaults');
          } else if (error.code === '42P01') { // Table doesn't exist
            console.warn('App settings table does not exist yet. Using default settings.');
          } else {
            console.error('Error fetching slow mode settings:', error);
          }
          return;
        }
        
        if (data) {
          setIsEnabled(data.value.enabled);
          setInterval(data.value.interval);
        }
      } catch (err) {
        console.error('Error in fetchSettings:', err);
      }
    };

    fetchSettings();
  }, []);
  
  const saveSettings = async () => {
    setLoading(true);
    try {
      // Save to localStorage as a backup
      localStorage.setItem('slow_mode_settings', JSON.stringify({
        enabled: isEnabled,
        interval: interval
      }));
      
      try {
        // Try to save to Supabase if the table exists
        const { error } = await supabase
          .from('application_settings')
          .upsert({ 
            key: 'slow_mode', 
            value: { enabled: isEnabled, interval: interval } 
          });
          
        if (error) {
          if (error.code === '42P01') { // Table doesn't exist
            console.warn('App settings table does not exist yet. Settings saved to localStorage only.');
            toast.warning('Settings saved locally (database table needs to be created)');
          } else {
            console.error('Failed to save settings:', error);
            toast.error('Failed to save to database: ' + error.message);
            return;
          }
        } else {
          toast.success('Slow mode settings updated in database');
        }
      } catch (dbErr) {
        console.error('Database error saving settings:', dbErr);
        toast.warning('Settings saved locally only (database error)');
      }
    } catch (err) {
      console.error('Error saving slow mode settings:', err);
      toast.error('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Bulletin Feed Slow Mode</CardTitle>
        <CardDescription>
          Control the rate at which bulletins appear in the feed to prevent overwhelming users
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="slow-mode-toggle">Enable Slow Mode</Label>
            <p className="text-sm text-muted-foreground">
              When enabled, bulletins will appear gradually in the feed
            </p>
          </div>
          <Switch 
            id="slow-mode-toggle"
            checked={isEnabled} 
            onCheckedChange={setIsEnabled} 
          />
        </div>
        
        <div className="space-y-3">
          <div>
            <Label htmlFor="interval-slider">Display Interval: {interval}ms</Label>
            <p className="text-sm text-muted-foreground mb-2">
              Time between posts appearing in the feed
            </p>
          </div>
          <Slider
            id="interval-slider"
            disabled={!isEnabled}
            value={[interval]}
            min={500}
            max={10000}
            step={500}
            onValueChange={(values) => setInterval(values[0])}
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Fast (500ms)</span>
            <span>Medium (5s)</span>
            <span>Slow (10s)</span>
          </div>
        </div>
        
        <Button 
          onClick={saveSettings} 
          disabled={loading}
          className="w-full"
        >
          {loading ? 'Saving...' : 'Save Slow Mode Settings'}
        </Button>
        
        <p className="text-xs text-muted-foreground mt-4">
          <strong>Note:</strong> Settings are saved to the application_settings table in Supabase.
        </p>
      </CardContent>
    </Card>
  );
};

export default SlowModeSettings;
